import { Component, OnInit } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { ToastService } from "../../services/notification/toast.service";
import { SpinnerService } from "../../services/spinner/spinner.service";
import { RbacService } from "../rbac.service";
import { UserPermissionsService } from "../../services/user-permissions.service";
import { TranslateService } from "@ngx-translate/core";
import { SecurityService } from "../../services/security.service";

interface Permission {
  id: number;
  code: string;
  description: string;
  summary: string;
  sensitive?: "normal" | "high";
  selected?: boolean;
}

interface PermissionModule {
  id: number;
  name: string;
  expanded: boolean;
  permissions: Permission[];
}

@Component({
  selector: "app-onboarding-user-role-create",
  templateUrl: "./onboarding-user-role-create.component.html",
  styleUrls: ["./onboarding-user-role-create.component.scss"],
  standalone: false
})
export class OnboardingUserRoleCreateComponent implements OnInit {
  private readonly maxRoleNameLength = 45;
  private readonly maxRoleDescriptionLength = 200;
  protected readonly createRolePermissions = [
    "controllers.Roles.create",
  ];
  protected readonly updateRolePermissions = [
    "controllers.Roles.update",
  ];
  roleForm: FormGroup;
  searchTerm = "";
  protected selectedPermissionIds = new Set<number>();

  modules: PermissionModule[] = [];

  constructor(
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private formBuilder: FormBuilder,
    protected toast: ToastService,
    private spinner: SpinnerService,
    private rbacService: RbacService,
    protected userPermissionsService: UserPermissionsService,
    protected translate: TranslateService,
    private securityService: SecurityService
  ) {
    this.roleForm = this.formBuilder.group({
      name: ["", [Validators.required, Validators.minLength(2), Validators.maxLength(this.maxRoleNameLength)]],
      description: ["", [Validators.maxLength(this.maxRoleDescriptionLength)]],
    });
  }

  ngOnInit(): void {
    this.loadPermissionsCatalog();
  }

  get selectedPermissions(): Permission[] {
    return this.modules.flatMap((module) =>
      module.permissions.filter((permission) => permission.selected)
    );
  }

  get totalPermissions(): number {
    return this.modules.reduce((total, module) => total + module.permissions.length, 0);
  }

  get selectedByModule(): { name: string; permissions: Permission[] }[] {
    return this.modules
      .map((module) => ({
        name: module.name,
        permissions: module.permissions.filter((permission) => permission.selected),
      }))
      .filter((module) => module.permissions.length > 0);
  }

  get previewItems(): string[] {
    return this.selectedPermissions.map((permission) =>
      `${permission.summary} (${permission.code})`
    );
  }

  get selectedModuleNames(): string[] {
    return this.selectedByModule.map((module) => module.name);
  }

  get visibleSelectedModuleNames(): string[] {
    return this.selectedModuleNames.slice(0, 4);
  }

  get hiddenSelectedModuleCount(): number {
    return Math.max(this.selectedModuleNames.length - this.visibleSelectedModuleNames.length, 0);
  }

  get compactPreviewText(): string {
    const permissionCount = this.selectedPermissions.length;
    const moduleCount = this.selectedModuleNames.length;
    const permissionLabel = this.translate.instant(
      permissionCount === 1 ? "onboardingUsersRoles.roleForm.permissionSingular" : "onboardingUsersRoles.roleForm.permissionPlural"
    );
    const moduleLabel = this.translate.instant(
      moduleCount === 1 ? "onboardingUsersRoles.roleForm.moduleSingular" : "onboardingUsersRoles.roleForm.modulePlural"
    );

    return this.translate.instant("onboardingUsersRoles.roleForm.summaryText", {
      permissionCount,
      permissionLabel,
      moduleCount,
      moduleLabel,
    });
  }

  get compactModulesText(): string {
    const moduleNames = this.visibleSelectedModuleNames.join(", ");
    if (!moduleNames) return "";

    return this.hiddenSelectedModuleCount > 0
      ? this.translate.instant("onboardingUsersRoles.roleForm.moreModules", {
          moduleNames,
          count: this.hiddenSelectedModuleCount,
        })
      : moduleNames;
  }

  visiblePermissions(module: PermissionModule): Permission[] {
    const query = this.searchTerm.trim().toLowerCase();
    if (!query) {
      return module.permissions;
    }

    return module.permissions.filter((permission) =>
      (permission.description || "").toLowerCase().includes(query) ||
      permission.code.toLowerCase().includes(query) ||
      module.name.toLowerCase().includes(query)
    );
  }

  filterPermissions(value: string) {
    this.searchTerm = value;
    if (!this.searchTerm.trim()) return;

    this.modules.forEach((module) => {
      module.expanded = this.visiblePermissions(module).length > 0;
    });
  }

  toggleModule(module: PermissionModule) {
    module.expanded = !module.expanded;
  }

  selectedCount(module: PermissionModule): number {
    return module.permissions.filter((permission) => permission.selected).length;
  }

  toggleModuleSelection(module: PermissionModule, event: Event) {
    event.stopPropagation();
    const shouldSelect = this.selectedCount(module) !== module.permissions.length;
    module.permissions.forEach((permission) => permission.selected = shouldSelect);
  }

  removePermission(permission: Permission, event?: Event) {
    event?.preventDefault();
    event?.stopPropagation();

    this.modules.forEach((module) => {
      module.permissions.forEach((modulePermission) => {
        if (Number(modulePermission.id) === Number(permission.id)) {
          modulePermission.selected = false;
        }
      });
    });
  }

  save() {
    if (!this.canSaveRole()) {
      this.toast.showWarning(this.translate.instant("onboardingUsersRoles.messages.noPermissionCreateRoles"));
      return;
    }

    if (this.roleForm.invalid) {
      this.toast.showWarning(this.translate.instant("onboardingUsersRoles.messages.enterRoleName"));
      return;
    }

    const partnerId = this.getPartnerId();
    if (!partnerId) {
      return this.toast.showInfo(this.translate.instant("onboardingUsersRoles.messages.missingPartnerSaveRole"));
    }

    this.spinner.open();
    const roleBody = {
      name: this.roleForm.value.name,
      description: this.roleForm.value.description,
    };

    this.rbacService.createRole(partnerId, roleBody).subscribe(
      (response) => this.addSelectedPermissions(partnerId, this.extractRoleId(response)),
      (error) => {
        this.spinner.close();
        this.toast.showError(error);
      }
    );
  }

  goBack() {
    this.router.navigate(["../../"], {
      relativeTo: this.activatedRoute,
      queryParamsHandling: "preserve",
    });
  }

  protected addSelectedPermissions(partnerId: number, roleId: number | string | null) {
    if (!roleId || this.selectedPermissions.length === 0) {
      this.spinner.close();
      this.toast.showSuccess(this.translate.instant("onboardingUsersRoles.messages.roleSaved"));
      this.goBackAfterToast();
      return;
    }

    const permissionIds = this.selectedPermissions.map((permission) => permission.id);
    this.rbacService.addPermissions(partnerId, roleId, permissionIds).subscribe(
      () => {
        this.spinner.close();
        this.toast.showSuccess(this.translate.instant("onboardingUsersRoles.messages.roleSaved"));
        this.goBackAfterToast();
      },
      (error) => {
        this.spinner.close();
        this.toast.showError(error);
      }
    );
  }

  protected extractRoleId(response: any): number | string | null {
    return response?.id ||
      response?.roleId ||
      response?.result?.id ||
      response?.result?.roleId ||
      response?.data?.id ||
      response?.data?.roleId ||
      null;
  }

  protected goBackAfterToast(): void {
    setTimeout(() => this.goBack(), 700);
  }

  canSaveRole(): boolean {
    return this.userPermissionsService.hasAnyPermission(this.createRolePermissions);
  }

  protected getPartnerId(): number | null {
    const partnerId = this.securityService.getTokenPartner();
    return partnerId ? Number(partnerId) : null;
  }

  protected loadPermissionsCatalog() {
    this.spinner.open();
    this.rbacService.listMenus().subscribe(
      (response) => {
        this.modules = this.extractMenus(response).map((menu: any) => ({
          id: menu.id,
          name: menu.name,
          expanded: true,
          permissions: (menu.permissions || []).map((permission: any) => ({
            id: permission.id,
            code: permission.name,
            description: permission.description || permission.name,
            summary: permission.description || permission.name,
            selected: this.selectedPermissionIds.has(Number(permission.id)),
          })),
        }));
        this.spinner.close();
      },
      (error) => {
        this.modules = [];
        this.spinner.close();
        this.toast.showError(error);
      }
    );
  }

  protected extractMenus(response: any): any[] {
    if (Array.isArray(response)) return response;
    if (Array.isArray(response?.data?.menus)) return response.data.menus;
    if (Array.isArray(response?.result?.data?.menus)) return response.result.data.menus;
    if (Array.isArray(response?.result)) return response.result;
    if (Array.isArray(response?.menus)) return response.menus;
    return [];
  }
}
